import React, { useCallback, useRef, useState } from 'react';
import BusinessDataGridItem from './BusinessDataGridItem';
import GridComponent from '../../../../../../BaseComponents/GridComponents/EDGrid';
import { EmptyReason } from '../EmptyReason/EmptyReason';
import { IExtendedBusinessDataProps } from '../BusinessDataSet/BusinessDataSet';
import EmptyFilteredComponent from '../EmptyFilteredComponent/EmptyFilteredComponent';
import EDVirtualGrid from '../../../../../../BaseComponents/GridComponents/EDVirtualGrid';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { times } from 'ramda';
import EDGrid from '../../../../../../BaseComponents/GridComponents/EDGrid';
import {
  breakpointIsSmallerOrEqual,
  useBreakpointContext,
} from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { FLOATING_MENU_CLEARANCE } from '../../../../../../Components/FloatingBottomArea/FloatingMenu';
import useRegisterScrollTarget from '../../../../../../Components/AppNavbar/useRegisterScrollTarget';

/**
 * Komponent som visar en grid med businessdata-kort.
 */

const BusinessDataGrid: React.FunctionComponent<
  React.PropsWithChildren<IExtendedBusinessDataProps>
> = props => {
  const [isScrolling, setIsScrolling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { localBreakPoint } = useBreakpointContext();
  //listan delas upp i antal kolumner beroende på breakpoint
  const itemsPerRow = breakpointIsSmallerOrEqual('sm', localBreakPoint)
    ? 1
    : breakpointIsSmallerOrEqual('md', localBreakPoint)
    ? 2
    : breakpointIsSmallerOrEqual('lg', localBreakPoint)
    ? 3
    : 4;

  const itemWidth = `${(100 / itemsPerRow).toFixed(2)}%`;
  const gridRowCount = Math.ceil(props.sortedData.rows.length / itemsPerRow);

  const minicard = useIsSmallScreen();
  const itemRender = useCallback(
    (rowindex: number) => {
      if (rowindex * itemsPerRow >= props.sortedData.rows.length) {
        return <div style={{ height: FLOATING_MENU_CLEARANCE }} />;
      }
      //rowIndex är index till rad i griden (som kan innehålla flera poster från listan)
      const indexes = times(i => rowindex * itemsPerRow + i, itemsPerRow); //index i listan som ska visas på aktuell rad (kan innehålla extra index på sista raden som hanteras nedan)
      return (
        <EDGrid sx={{ display: 'flex' }}>
          {indexes.map(index => {
            const item = props.sortedData.rows[index];
            if (!item) {
              return undefined;
            }
            return (
              <EDGrid key={item.id} sx={{ width: itemWidth }}>
                <BusinessDataGridItem
                  key={item.id}
                  item={item}
                  prevId={props.sortedData.rows.at(index - 1)?.id ?? null}
                  nextId={props.sortedData.rows.at(index + 1)?.id ?? null}
                  collection={props.collection}
                  associationName={props.association?.AssociationName}
                  columns={props.columns}
                  urval={props.urval}
                  selectDetail={props.selectDetail}
                  selected={
                    props.selected.includes(item.id) ||
                    props.selectedId === item.id
                  }
                  toggleSelected={props.toggleSelected}
                  showCheckbox={props.showCheckboxes}
                  businessIcons={props.businessIcons}
                  disabled={props.disabled}
                  colors={props.colors}
                  useFunktionsMenyItems={props.useFunktionsMenyItems}
                  hideDefaultFunktionsMenyItems={
                    props.hideDefaultFunktionsMenyItems
                  }
                  isMiniCard={minicard}
                  isScrolling={isScrolling}
                />
              </EDGrid>
            );
          })}
        </EDGrid>
      );
    },
    [
      itemsPerRow,
      props.sortedData.rows,
      props.collection,
      props.association?.AssociationName,
      props.columns,
      props.urval,
      props.selectDetail,
      props.selected,
      props.selectedId,
      props.toggleSelected,
      props.showCheckboxes,
      props.businessIcons,
      props.disabled,
      props.colors,
      props.useFunktionsMenyItems,
      props.hideDefaultFunktionsMenyItems,
      itemWidth,
      minicard,
      isScrolling,
    ]
  );

  const spacerRowCount = props.floatingMenuBottomSpacing ? 1 : 0;

  useRegisterScrollTarget({
    containerRef,
    selector: '[data-virtuoso-scroller="true"]',
    enabled: !props.selectedId,
  });

  return (
    <div ref={containerRef} style={{ height: '100%' }}>
      {props.sortedData.rows.length > 0 ? (
        <EDVirtualGrid
          totalCount={gridRowCount + spacerRowCount}
          item={itemRender}
          useVirtual
          setIsScrolling={setIsScrolling}
        />
      ) : (
        <GridComponent container alignItems="stretch" spacing={0}>
          {props.sortedData.filtered ? (
            <EmptyFilteredComponent />
          ) : (
            <EmptyReason
              urval={props.urval}
              setUrval={props.setUrval}
              collection={props.collection}
              refetch={props.refetch}
              hasData={props.sortedData.rows.length > 0}
              loading={props.loading}
            />
          )}
        </GridComponent>
      )}
    </div>
  );
};

export default BusinessDataGrid;
